/**
 * Single source of truth for "may this user operate across branches?".
 *
 * Two surfaces consume it and MUST agree, or the UI and the wire
 * disagree:
 *  - `BranchSelector` — whether the header picker renders at all.
 *  - `useBranchOfficeId` — whether the persisted `selectedBranchOfficeId`
 *    is honored, which decides the `X-Branch-Office-Id` header the API
 *    service injects on every request.
 *
 * Both used to compute `roleName === Admin || SuperAdmin` inline. That
 * dropped the backend's `users.access_all_branches` grant entirely: a
 * `manager` / `sommelier` / `cashier` flagged `access_all_branches = true`
 * (KON CAFE KITCHEN operations managers, CARNYBRASA sommelier) got no
 * selector and was pinned to their home branch, even though
 * `effective-role.service.ts` + `permission.guard.ts` already honor the
 * flag server-side. Keep this the ONLY place the question is answered.
 */
import { BRANCH_UNRESTRICTED_ROLES, RoleName } from '@neogeniux/api-contracts';
import { useAuthStore } from '../store/auth.store';

/**
 * True for roles that reach EVERY branch implicitly (admin / super_admin),
 * mirroring `BranchAccessService.isBranchUnrestricted` on the backend.
 * Only these users may pick the business-wide "all branches" view: a
 * multi-branch user granted 2 of 4 branches must not see aggregates that
 * include the other 2.
 */
export const useIsBranchUnrestricted = (): boolean => {
  const roleName = useAuthStore((state) => state.user?.role?.name);
  return BRANCH_UNRESTRICTED_ROLES.includes(roleName as RoleName);
};

/**
 * Whether the user may operate on more than their home branch. Which
 * branches they may switch TO is the access set from
 * `GET /users/me/branches` (`useMyBranches`), not this flag.
 */
export const useCanSwitchBranch = (): boolean => {
  const isUnrestricted = useIsBranchUnrestricted();
  const accessBranches = useAuthStore((state) => state.user?.accessBranches);
  return isUnrestricted || !!accessBranches;
};
